import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../context/LanguageContext';
import { getDistance } from '../services/api';
import RideMap from '../components/RideMap';
import AddressInput from '../components/AddressInput';

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 10) / 10;
}

export default function BookingPage({ user, onConfirm, onAuthNeeded, onBack }) {
  const [from, setFrom]               = useState({ text: '', lat: null, lng: null });
  const [to, setTo]                   = useState({ text: '', lat: null, lng: null });
  const [distanceKm, setDistanceKm]   = useState('');
  const [loading, setLoading]         = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [distanceError, setDistanceError] = useState('');
  const { t } = useLang();
  const rf = t.rideForm;

  const km    = parseFloat(distanceKm) || 5;
  const price = (km * 2).toFixed(2);

  useEffect(() => {
    if (from.lat && to.lat) {
      setDistanceKm(String(haversineKm(from.lat, from.lng, to.lat, to.lng)));
      setDistanceError('');
    }
  }, [from.lat, from.lng, to.lat, to.lng]);

  const abortRef = useRef(null);

  const handleFromSelect = ({ text, lat, lng }) => setFrom({ text, lat, lng });
  const handleToSelect   = ({ text, lat, lng }) => setTo({ text, lat, lng });

  const handleBlurFallback = async () => {
    if (!from.text.trim() || !to.text.trim()) return;
    if (from.lat && to.lat) return; // already have coords from autocomplete

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    setCalculating(true);
    setDistanceError('');
    try {
      const res = await getDistance(from.text, to.text, signal);
      setDistanceKm(String(res.data.distanceKm));
      if (res.data.fromCoords) setFrom((p) => ({ ...p, lat: res.data.fromCoords[0], lng: res.data.fromCoords[1] }));
      if (res.data.toCoords)   setTo((p)   => ({ ...p, lat: res.data.toCoords[0],   lng: res.data.toCoords[1]   }));
    } catch (err) {
      if (err.code === 'ERR_CANCELED') return;
      setDistanceError(t.booking?.distanceError || 'Could not calculate distance. Enter manually.');
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const booking = {
      fromLocation: from.text,
      toLocation: to.text,
      distanceKm: distanceKm ? parseFloat(distanceKm) : undefined,
      fromLat: from.lat || undefined,
      fromLng: from.lng || undefined,
      toLat: to.lat || undefined,
      toLng: to.lng || undefined,
    };

    if (!user) {
      onAuthNeeded(booking);
      return;
    }

    setLoading(true);
    try {
      await onConfirm(booking);
    } finally {
      setLoading(false);
    }
  };

  const coords = from.lat && to.lat ? { from: [from.lat, from.lng], to: [to.lat, to.lng] } : null;

  return (
    <div className="bg-slate-900 min-h-screen">
    <div className="max-w-2xl mx-auto px-6 py-12">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-white/40 hover:text-white mb-6 transition-colors">
        {t.client.back}
      </button>

      <h1 className="text-2xl font-black text-white mb-2">{rf.title}</h1>
      <p className="text-white/50 text-sm mb-6">{t.booking?.subtitle || 'Fill in the details and confirm your ride.'}</p>

      {/* Map */}
      {coords && (
        <div className="mb-6">
          <RideMap
            fromCoords={coords.from}
            toCoords={coords.to}
            fromLabel={from.text}
            toLabel={to.text}
          />
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 space-y-5">
        <div className="bg-white/10 rounded-2xl p-4 space-y-3 border border-white/10">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-green-400 shrink-0" />
            <AddressInput
              value={from.text}
              onChange={(text) => { setFrom({ text, lat: null, lng: null }); setDistanceKm(''); }}
              onSelect={handleFromSelect}
              onBlur={handleBlurFallback}
              placeholder={rf.pickup}
              required
            />
          </div>
          <div className="ml-1.5 border-l-2 border-dashed border-white/20 h-4" />
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-red-400 shrink-0" />
            <AddressInput
              value={to.text}
              onChange={(text) => { setTo({ text, lat: null, lng: null }); setDistanceKm(''); }}
              onSelect={handleToSelect}
              onBlur={handleBlurFallback}
              placeholder={rf.dropoff}
              required
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-white/60 mb-0">
              {rf.distLabel} <span className="text-white/30 font-normal">{rf.distHint}</span>
            </label>
            {calculating && (
              <span className="text-xs text-primary-400 flex items-center gap-1">
                <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
                {t.booking?.calculating || 'Calculating…'}
              </span>
            )}
          </div>
          <input
            value={distanceKm}
            onChange={(e) => { setDistanceKm(e.target.value); setDistanceError(''); }}
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            placeholder={rf.distPlaceholder}
            readOnly={calculating}
            className={`w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${calculating ? 'opacity-50 cursor-wait' : ''}`}
          />
          {distanceError && (
            <p className="text-xs text-amber-400 mt-1">{distanceError}</p>
          )}
        </div>

        {from.text && to.text && (
          <div className="flex items-center justify-between bg-primary-600/20 border border-primary-500/30 rounded-xl px-5 py-3.5">
            <div className="text-sm text-white/60">
              <span className="font-medium text-white">{km} km</span> · {rf.perKm}
            </div>
            <div className="font-black text-primary-400 text-xl">€{price}</div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || calculating}
          className="btn-primary w-full justify-center py-3.5 disabled:opacity-50 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary-600/30"
        >
          {loading ? rf.booking : user ? rf.confirm : t.booking?.signInToBook || 'Sign in to book'}
        </button>

        {!user && (
          <p className="text-center text-xs text-white/40">
            {t.booking?.loginHint || "You'll be asked to sign in before confirming"}
          </p>
        )}
      </form>
    </div>
    </div>
  );
}
